import * as React from "react";
import {authorize} from "@athena-portal/components";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import {Link} from "react-router-dom";
import * as moment from "moment";
import {InfoModal} from "../../../../components/modals/InfoModal";
import {PricingExplainedInfo} from "../../../../components/PricingExplainedInfo";
import {
    FalconNavigatorHeader,
    Policy,
    productHelper,
    ProductsEnum,
    Quote,
    resourcesHelper,
    Subscription,
    INTERACTION_PROCESS_CHANGE_ADDRESS,
    INTERACTION_PROCESS_MTA_ADD_CAR,
    INTERACTION_PROCESS_NB
} from "@falcon/services";
import {renderPricingExplained, renderRentersPricingExplained} from "@falcon/plugin-falcon-journey";
import {getAmountToDisplay, getAmountWithDecimalDigits} from "../../../../CommonFunctions";

interface IProps {
    quotes?: Quote[];
    policies?: Policy[];
    isActivePoliciesPresent: boolean;
    subscription?: Subscription;
}

@authorize
export class YourQuotesPanel extends React.Component<IProps> {

    private pluginTemplates = new PluginTemplates();
    private readonly MOTOR_PRODUCT: string = "MotorInsurance";
    private readonly RENTERS_PRODUCT: string = "RentersInsurance";
    private readonly FLOW_SUBSCRIPTION_PRODUCT: string = "FlowSubscription";
    private SHOW_ONLY: string | null;

    constructor(props: IProps) {
        super(props);
        this.setShowOnly = this.setShowOnly.bind(this);
        this.setShowOnly();
    }

    public render() {
        return (
            <React.Fragment>
                {this.renderQuotes()}
            </React.Fragment>
        );
    }

    private renderQuotes(): JSX.Element[] | JSX.Element {
        const carQuotes: any = [];
        const rentersQuotes: any = [];

        // Filter quotes which don't have UW and are not from aggregator.
        const filteredQuotes = this.props.quotes &&
            this.props.quotes.filter(quote => {
                return !(Array.isArray(quote.interaction.underwritingResult) && quote.interaction.underwritingResult.length > 0 &&
                    quote.interaction.source === null)
            });

        if (filteredQuotes && filteredQuotes.length !== 0) {
            filteredQuotes.forEach(
                quote => {
                    const summary: string = quote.interaction.process === INTERACTION_PROCESS_MTA_ADD_CAR ? "additionalVehicleSummaryPage" : "summary";
                    let process: string = this.props.isActivePoliciesPresent ? quote.interaction.process + "/" + summary : "nb/summary";
                    let product: string = "";

                    if (quote.interaction.vehicles && quote.interaction.vehicles.length !== 0) {
                        const nextPaymentDate: moment.Moment = this.getNextPaymentDate(
                                quote.interaction.vehicles[0]?.factors?.constraintEndDate, quote.interaction.headerInfo.startDate);
                        this.setSubscriptionDetails(nextPaymentDate, quote);

                        if (quote.product === ProductsEnum.MOTOR) {
                            const motorQuoteLink: string = this.getViewAndEditDetailsLink(this.MOTOR_PRODUCT, process, quote);

                            if (this.SHOW_ONLY) {
                                if (this.SHOW_ONLY === ProductsEnum.MOTOR) {
                                    this.setQuotesContent(quote, carQuotes, motorQuoteLink, nextPaymentDate);
                                }
                            } else {
                                this.setQuotesContent(quote, carQuotes, motorQuoteLink, nextPaymentDate);
                            }

                        }
                    } else if (quote.interaction.realEstates && quote.interaction.realEstates.length !== 0 ) {
                        product = this.RENTERS_PRODUCT;
                        process = quote.interaction.process + "/" + "quote";
                        const nextPaymentDate: moment.Moment = this.getNextPaymentDate(
                                quote.interaction.realEstates[0].realEstateFactors.constraintEndDate, quote.interaction.headerInfo.startDate);
                        const rentersQuoteLink: string = this.getViewAndEditDetailsLink(product, process, quote);

                        if (this.SHOW_ONLY) {
                            if (this.SHOW_ONLY === ProductsEnum.RENTERS) {
                                this.setQuotesContent(quote, rentersQuotes, rentersQuoteLink, nextPaymentDate);
                            }
                        } else {
                            this.setQuotesContent(quote, rentersQuotes, rentersQuoteLink, nextPaymentDate);
                        }
                    }
                }
            );
            return (
                <div>
                    {((carQuotes && carQuotes.length > 0) || (rentersQuotes && rentersQuotes.length > 0)) && (
                        <div className="journey-content">
                            <div className="journey-questions">
                                <FalconNavigatorHeader back={this.getBackLink()} title="Your quote(s)"/>
                                {carQuotes}
                                {rentersQuotes}
                                {this.pluginTemplates.renderBackLink("Back", false, this.getBackLink())}
                            </div>
                        </div>
                    )}
                </div>
            )
        } else {
            return (
                <div className="journey-content">
                    <div className="journey-questions">
                        {this.pluginTemplates.renderBackLink("Back", true, this.getBackLink())}
                        <div className="quote quote-summary-panel plugin text-center mb-4 mt-4">
                            <p>You don’t have any active quotes saved</p>
                        </div>
                    </div>
                    {this.pluginTemplates.renderBackLink("Back", false, this.getBackLink())}
                </div>
            )
        }
    }

    private getViewAndEditDetailsLink = (product: string, process: string, quote: Quote): string => {
        if (quote.interaction.process === INTERACTION_PROCESS_CHANGE_ADDRESS) {
            product = this.FLOW_SUBSCRIPTION_PRODUCT;
        }
        return "/journey/show/product/" + product + "/process/" + process + "/" + quote.interaction._id + "?auth";
    }

    private getNextPaymentDate = (constraintEndDate: string, startDate: string): moment.Moment => {
        return constraintEndDate ? moment(constraintEndDate) : moment(startDate).add(3, "M").subtract(1, 'd');
    }

    private setSubscriptionDetails = (nextPaymentDate: moment.Moment, quote: Quote): void => {
        const subscription: Subscription = new Subscription;
        subscription.nextPaymentDate = nextPaymentDate.format("YYYY-MM-DD");
        subscription.lastPaymentAmount = quote.interaction.factors.premium;
        subscription.lastNeverHigherAmount = quote.interaction.factors.neverHigher;
    }

    private setQuotesContent = (quote: Quote, productQuotes: any[], viewAndEditLink: string, nextPaymentDate: moment.Moment): void => {
        productQuotes.push(
            <div className="quote quote-summary-panel plugin text-center mb-4" key={quote.interaction._id}>
                <div className="summary-section pb-0 mb-3">
                    <div className="quote-icon"/>
                    {quote.interaction.process === INTERACTION_PROCESS_NB ? this.renderNewBusinessPricing(quote.interaction) : this.renderMtaPricing(quote.interaction)}

                    <p className="text-center mx-4 mb-0 text-blue-2">
                        Your price could go up or down after this – but we guarantee you won't pay more
                        than
                    </p>
                    {quote.interaction.product === ProductsEnum.MOTOR
                        ?
                        <p className="text-center font-weight-bold mx-4 text-blue-2">
                            {`£${quote.interaction.factors.neverHigher} per month until ${nextPaymentDate.format("D MMMM YYYY")}`}
                        </p>
                        :
                        <p className="text-center font-weight-bold mx-4 text-blue-2">
                            {`${getAmountWithDecimalDigits(quote.interaction.factors.neverHigher)} per month until ${nextPaymentDate.format("D MMMM YYYY")}`}
                        </p>
                    }
                </div>
                {quote.interaction.product === ProductsEnum.MOTOR
                    ?
                    <React.Fragment>
                        <div className="summary-section pb-0 mb-3">{this.renderCarsAndDrivers(quote)}</div>
                        <div className="summary-section pb-0 mb-3">{this.renderAllDrivers(quote)}</div>
                    </React.Fragment>
                    :
                    <React.Fragment>
                        <div className="summary-section pb-0 mb-3">{this.renderPropertyDetails(quote)}</div>
                        <div className="summary-section pb-0 mb-3">{this.renderRenterDetails(quote)}</div>
                    </React.Fragment>
                }
                <div className={"buttonLinkGrid"}>
                    <Link
                        to={viewAndEditLink}
                        className="inner-content"
                        id={"viewAndEditQuote" + quote.quoteId}>
                        <button type="button" className="btn btn-primary btn-next plugin">View and edit
                            details
                        </button>
                    </Link>
                </div>
                <div className="inner-content muli-light-16 center">
                    <span className="preserve">Quote expires on </span>
                    <b>{this.renderQuoteExpiryDate(quote)}</b>
                </div>
            </div>
        );
    }

    private getBackLink(): string {
        let onlyAnnualMotorPolicyPresent: boolean = false;
        let onlySubscriptionProduct: boolean = false;

        if (this.props.policies) {
            onlyAnnualMotorPolicyPresent = (this.props.subscription?.allPoliciesAreCancelled && productHelper.includesOnlyWolfMotorProduct(this.props.policies!)) ||
                productHelper.includesOnlyNonExpireWolfMotorProduct(this.props.policies!);

            onlySubscriptionProduct = productHelper.includesOnlyNonExpireMonthlySubscriptionProduct(this.props.policies!);
        }

        if (onlyAnnualMotorPolicyPresent) {
            return "/dashboard/viewOrGetQuotes"
        } else if(onlySubscriptionProduct) {
            return "/dashboard/"
        } else {
            return "/dashboard/viewOrGetQuotes";
        }
    }

    private renderNewBusinessPricing(interaction: any): JSX.Element {
        return (
            <div>
                <div id="price" className="premium font-weight-bold pt-4">
                    {getAmountToDisplay(interaction.factors.premium)}
                </div>
                <p className="text-center">For the first month
                    {this.renderPricingExplainedModal(interaction)}
                </p>
            </div>
        )
    }

    private renderMtaPricing(interaction: any): JSX.Element {
        if (interaction.factors.additionalPremium && interaction.factors.additionalPremium > 0) {
            return (
                <div>
                    <div id="price" className="premium font-weight-bold pt-4">
                        {getAmountToDisplay(interaction.factors.additionalPremium)}
                    </div>
                    <p className="text-center">To cover the rest of {moment().format("MMMM")}
                        {this.renderPricingExplainedModal(interaction)}
                    </p>
                </div>
            )
        } else if (interaction.factors.returnPremium && interaction.factors.returnPremium > 0) {
            return (
                <div>
                    <div id="price" className="premium font-weight-bold pt-4">
                        {getAmountToDisplay(interaction.factors.returnPremium)}
                    </div>
                    <p className="text-center font-weight-bold">Refund after the vehicle change
                        {this.renderPricingExplainedModal(interaction)}
                    </p>
                </div>
            )
        } else {
            return (
                <div className="inner-container">
                    <h2 className="inner-container blue-panel-title">Your new cover</h2>
                    <p className="text-center font-weight-bold">Good news, there is no extra cost for the change you made!</p>
                </div>
            )
        }
    }

    private renderQuoteExpiryDate(quote: Quote): string | null {
        const createdOnDate = moment(quote.interaction.createdOn);
        const coverStartDate = moment(quote.interaction.headerInfo.startDate);

        if (createdOnDate.isBefore(coverStartDate)) {
            return PluginTemplates.convertDateUTC(quote.interaction.headerInfo.startDate, "DD MMMM YYYY")
        } else {
            return PluginTemplates.convertDateUTC(quote.interaction.createdOn, "DD MMMM YYYY")
        }
    }

    private renderRenterDetails(quote: Quote): JSX.Element {
        return (
            <div>
                <h3>Account holder</h3>
                <div className="mb-3">
                    <img className="driver-icon mb-3" src="/img/driver-icon.svg" alt="driver icon"/>
                    <div>{quote.interaction.policyHolder.firstName} {quote.interaction.policyHolder.lastName}</div>
                </div>
            </div>
        );
    }

    private renderPropertyDetails(quote: Quote): JSX.Element {
        return (
            <div>
                <h3>Property details</h3>
                <div className="mb-3">
                    <img className="driver-icon mb-3" src="/img/Flow-renters.svg" alt="renter icon"/>
                    <div>{quote.interaction.policyHolder.address.fullAddress}</div>
                </div>
            </div>
        );
    }

    private renderAllDrivers(quote: Quote): JSX.Element {
        const drivers: any = [];
        drivers.push(
            <div className="mb-3" key={quote.interaction.policyHolder.personId}>
                <img className="driver-icon mb-3" src="/img/driver-icon.svg" alt="driver icon"/>
                <div>{quote.interaction.policyHolder.firstName} {quote.interaction.policyHolder.lastName}</div>
            </div>
            );
        if (quote.interaction.additionalDrivers && quote.interaction.additionalDrivers.length > 0) {
            for (const additionalDriver of quote.interaction.additionalDrivers) {
                drivers.push(
                    <div className="mb-3" key={additionalDriver.driverId}>
                        <img className="driver-icon mb-3" src="/img/driver-icon.svg" alt="driver icon"/>
                        <div>{additionalDriver.firstName} {additionalDriver.lastName}</div>
                    </div>
                )
            }
        }
        return (
            <div>
                <h3>Driver(s)</h3>
                {drivers}
            </div>
        )
    }

    private renderCarsAndDrivers(quote: Quote): JSX.Element {
        const vehicles: any = [];

        for (const vehicle of quote.interaction.vehicles) {
            vehicles.push(
                <div className="inner-content car-text" key={vehicle.registrationNumber}>
                    <img className="vehicle-icon mb-3" alt="Vehicle icon" src={`/${resourcesHelper.THEME}/img/wolf-car-icon.png`} aria-label="Vehicle icon"/>
                    <div className="pb-3">{vehicle.make}, {vehicle.model}{vehicle.registrationNumber && ` (${vehicle.registrationNumber})`}</div>
                    <div className="pb-3">
                        <div className="mb-2"><b>Driver(s)</b></div>
                        {getDrivers(vehicle)}
                    </div>
                </div>
            );
        }

        function getDrivers(vehicle: any) {
            const drivers: any = [];
            if (vehicle.mainDriverId) {
                drivers.push(
                    <div key={vehicle.mainDriverId}>{getDriverInfo(vehicle.mainDriverId)} <b>(main driver)</b></div>
                );
            }

            for (const driverId of vehicle.driverIds) {
                if (quote.interaction.additionalDrivers && quote.interaction.additionalDrivers.length !== 0 && driverId !== vehicle.mainDriverId) {
                    drivers.push(
                        <div key={driverId}>
                            {getDriverInfo(driverId)}
                        </div>
                    );
                }
            }
            return drivers;
        }

        function getDriverInfo(driverId: any) {
            if (driverId === quote.interaction.policyHolder.personId) {
                return (
                    <span>{quote.interaction.policyHolder.firstName} {quote.interaction.policyHolder.lastName}</span>
                );
            }
            let driverInfo;
            for (const driver of quote.interaction.additionalDrivers) {
                if (driverId === driver.personId) {
                    driverInfo =
                        <span>{driver.firstName} {driver.lastName}</span>
                }
            }
            return driverInfo;
        }

        return (
            <div>
                <h3>Car(s)</h3>
                {vehicles}
            </div>
        );
    };

    private renderPricingExplainedModal(interaction: any): JSX.Element {
        let element: JSX.Element = <PricingExplainedInfo quoteInteraction={interaction}/>;

        if (interaction.process === INTERACTION_PROCESS_NB) {
            if (productHelper.isMotor(interaction)) {
                element = renderPricingExplained(interaction);
            } else if (productHelper.isRenters(interaction)) {
                element = renderRentersPricingExplained(interaction);
            }
        }

        return (
            <InfoModal className="pl-3" title="Pricing explained">{element}</InfoModal>
        )
    }

    private setShowOnly(): void {
        this.SHOW_ONLY = sessionStorage.getItem("SHOW_ONLY");
        sessionStorage.removeItem("SHOW_ONLY");
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/quotesPage/YourQuotesPanel.tsx